import { useEffect, useMemo, useState } from "react"
import { Block, LabelMedium, Skeleton, useStyletron } from "@mezo-org/mezo-clay"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { useSwapRate } from "#/shared/hooks"
import { SwappableToken } from "#/shared/types"
import { floatToBigInt, getCurrencySymbol } from "#/shared/utils"

type SwapModalRateProps = {
  amountIn: string
  amountOut: string
  tokenIn: SwappableToken
  tokenOut: SwappableToken
  isFieldActive: boolean
}

export default function SwapModalRate({
  amountIn,
  amountOut,
  tokenIn,
  tokenOut,
  isFieldActive,
}: SwapModalRateProps) {
  const [css, theme] = useStyletron()

  const { getSwapRate } = useSwapRate(tokenIn, tokenOut)

  const tokenInDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenIn),
  )

  const tokenOutDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenOut),
  )

  const [swapRate, setSwapRate] = useState<string | null>(null)

  useEffect(() => {
    const fetchSwapRate = async () => {
      if (!amountOut || !amountIn || isFieldActive) return

      const rate = await getSwapRate(floatToBigInt(amountIn, tokenInDecimals))

      setSwapRate(rate)
    }

    fetchSwapRate()
  }, [
    amountIn,
    amountOut,
    tokenIn,
    tokenOut,
    tokenInDecimals,
    tokenOutDecimals,
    getSwapRate,
    isFieldActive,
  ])

  const swapRateContent = useMemo(
    () =>
      swapRate ? (
        <LabelMedium as="span">
          1 {getCurrencySymbol(tokenIn)} ~ {swapRate}{" "}
          {getCurrencySymbol(tokenOut)}
        </LabelMedium>
      ) : (
        <Skeleton
          animation
          height={theme.sizing.scale800}
          width={theme.sizing.scale4800}
        />
      ),
    [swapRate, tokenIn, tokenOut, theme.sizing],
  )

  return (
    <Block
      className={css({
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${theme.sizing.scale500} 0`,
      })}
    >
      <LabelMedium color={theme.colors.contentSecondary} as="span">
        Rate:
      </LabelMedium>
      {swapRateContent}
    </Block>
  )
}
